/* ================================================================
   api.ts — minimal fetch wrapper for the same-origin /api/proxy.
   No axios dependency; the home app only needs JSON POST/GET for auth.
   ================================================================ */

import { API_PROXY_BASE } from '../config/authConfig';

const STORAGE_SESSION_ID = 'X-Session-ID';

/**
 * Hard ceiling on a single request. API Gateway caps integration time at 29s, so
 * anything past this is a broken hop rather than a slow backend — surface it as
 * an error instead of leaving the caller's spinner running forever.
 */
const REQUEST_TIMEOUT_MS = 45_000;

export interface ApiResponse {
  status: number;
  data: any;
}

/** AxiosError-like shape so the ported authService error handling reads naturally. */
export class ApiError extends Error {
  status?: number;
  data?: any;
  constructor(message: string, status?: number, data?: any) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

function generateSessionId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 15)}`;
}

/** Mirror of the app's session id: stable per browser, sent as X-Session-ID. */
function getOrCreateSessionId(): string {
  try {
    let id = localStorage.getItem(STORAGE_SESSION_ID);
    if (!id) {
      id = generateSessionId();
      localStorage.setItem(STORAGE_SESSION_ID, id);
    }
    return id;
  } catch {
    return '';
  }
}

async function request(method: 'GET' | 'POST', path: string, body?: unknown): Promise<ApiResponse> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  const sessionId = getOrCreateSessionId();
  if (sessionId) headers[STORAGE_SESSION_ID] = sessionId;

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

  let res: Response;
  try {
    res = await fetch(`${API_PROXY_BASE}/${path}`, {
      method,
      headers,
      signal: controller.signal,
      ...(method === 'GET' ? {} : { body: JSON.stringify(body ?? {}) }),
    });
  } catch (err) {
    if ((err as Error)?.name === 'AbortError') {
      throw new ApiError('The server took too long to respond. Please try again.');
    }
    throw new ApiError('Network error. Please check your connection and try again.');
  } finally {
    clearTimeout(timer);
  }

  const text = await res.text();
  let data: any = text;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = text; // leave as raw string (some token endpoints return a bare JSON string)
    }
  } else {
    data = null;
  }

  if (!res.ok) {
    throw new ApiError(`Request failed with status ${res.status}`, res.status, data);
  }
  return { status: res.status, data };
}

export const apiPost = (path: string, body?: unknown): Promise<ApiResponse> => request('POST', path, body);
export const apiGet = (path: string): Promise<ApiResponse> => request('GET', path);
